import React, { useState, useEffect } from 'react'
import { X, ShoppingBag, Gift, Heart } from 'lucide-react'
import playCreateStorageService from '../services/playCreateStorageService'
import toast from 'react-hot-toast'

export const GiftPanel = ({ character, onClose, onGiftGiven }) => {
  const [activeTab, setActiveTab] = useState('shop') // 'shop' or 'inventory'
  const [gifts, setGifts] = useState([])
  const [userGifts, setUserGifts] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        const [allGifts, inventory] = await Promise.all([
          playCreateStorageService.getAllGifts(),
          playCreateStorageService.getUserGifts()
        ])
        setGifts(allGifts)
        setUserGifts(inventory)
      } catch (error) {
        console.error('❌ 加载数据失败:', error)
        toast.error('加载数据失败')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [character])

  const handlePurchase = async (giftId) => {
    try {
      const updatedInventory = await playCreateStorageService.purchaseGift('guest', giftId)
      setUserGifts(updatedInventory)
      toast.success('购买成功！')
    } catch (error) {
      console.error('❌ 购买礼物失败:', error)
      toast.error(error.message || '购买失败')
    }
  }

  const handleGiveGift = async (giftId) => {
    try {
      const gift = gifts.find(g => g.id === giftId)
      if (!gift) {
        toast.error('礼物不存在')
        return
      }
      
      const result = await playCreateStorageService.giveGiftToCharacter('guest', character.id, giftId)
      const updatedInventory = result.userGifts || await playCreateStorageService.getUserGifts()
      setUserGifts(updatedInventory)
      
      // 传递礼物信息给回调
      onGiftGiven(gift)
      onClose()
    } catch (error) {
      console.error('❌ 赠送礼物失败:', error)
      toast.error(error.message || '赠送失败')
    }
  }

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return 'text-gray-400 border-gray-500'
      case 'rare': return 'text-blue-400 border-blue-500'
      case 'epic': return 'text-purple-400 border-purple-500'
      case 'legendary': return 'text-yellow-400 border-yellow-500'
      default: return 'text-gray-400 border-gray-500'
    }
  }

  const ownedGifts = userGifts?.gifts || []

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg w-full max-w-4xl max-h-[80vh] flex flex-col border border-dark-600">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Gift size={24} className="text-primary" />
            礼物商城
          </h2>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* 标签页 */}
        <div className="flex border-b border-dark-600">
          <button
            onClick={() => setActiveTab('shop')}
            className={`flex-1 px-6 py-3 font-medium transition-colors ${
              activeTab === 'shop'
                ? 'text-primary border-b-2 border-primary'
                : 'text-dark-300 hover:text-white'
            }`}
          >
            <ShoppingBag size={18} className="inline mr-2" />
            商城
          </button>
          <button
            onClick={() => setActiveTab('inventory')}
            className={`flex-1 px-6 py-3 font-medium transition-colors ${
              activeTab === 'inventory'
                ? 'text-primary border-b-2 border-primary'
                : 'text-dark-300 hover:text-white'
            }`}
          >
            <Gift size={18} className="inline mr-2" />
            我的礼物 ({ownedGifts.reduce((sum, g) => sum + g.quantity, 0)})
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center text-dark-400 py-20">加载中...</div>
          ) : activeTab === 'shop' ? (
            <>
              {/* 货币显示 */}
              <div className="mb-4 p-4 bg-dark-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-dark-300">当前货币</span>
                  <span className="text-2xl font-bold text-yellow-400">
                    {userGifts?.currency || 0} 金币
                  </span>
                </div>
              </div>

              {/* 礼物列表 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {gifts.map((gift) => (
                  <div
                    key={gift.id}
                    className={`bg-dark-700 rounded-xl p-4 border-2 ${getRarityColor(gift.rarity)} hover:shadow-lg hover:shadow-primary/20 transition-all transform hover:scale-105`}
                  >
                    <div className="text-center mb-3">
                      <div className="w-20 h-20 mx-auto bg-dark-600 rounded-xl mb-3 flex items-center justify-center border-2 border-dark-500 shadow-inner">
                        {gift.imageUrl ? (
                          <img src={gift.imageUrl} alt={gift.name} className="w-full h-full object-cover rounded-lg" />
                        ) : (
                          <Gift size={40} className="text-dark-400" />
                        )}
                      </div>
                      <h3 className="font-bold text-white text-lg">{gift.name}</h3>
                      <p className="text-xs text-dark-400 mt-1">{gift.description}</p>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm bg-dark-600/50 rounded-lg px-3 py-2">
                        <span className="text-pink-400 font-medium flex items-center gap-1">
                          <Heart size={14} />
                          好感度 +{gift.effect.favorabilityBonus}
                        </span>
                        <span className="text-yellow-400 font-bold flex items-center gap-1">
                          💰 {gift.price}
                        </span>
                      </div>

                      <button
                        onClick={() => handlePurchase(gift.id)}
                        disabled={(userGifts?.currency || 0) < gift.price}
                        className="w-full px-4 py-2 bg-gradient-to-r from-primary to-secondary hover:from-primary/80 hover:to-secondary/80 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-md hover:shadow-lg"
                      >
                        🛒 购买
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : activeTab === 'inventory' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ownedGifts.length === 0 ? (
                <div className="col-span-full text-center text-dark-400 py-20">
                  还没有礼物，去商城购买吧！
                </div>
              ) : (
                ownedGifts.map((item) => {
                  const gift = gifts.find(g => g.id === item.giftId)
                  if (!gift) return null

                  return (
                    <div
                      key={item.giftId}
                      className={`bg-dark-700 rounded-lg p-4 border-2 ${getRarityColor(gift.rarity)}`}
                    >
                      <div className="text-center mb-3">
                        <div className="w-16 h-16 mx-auto bg-dark-600 rounded-lg mb-2 flex items-center justify-center">
                          {gift.imageUrl ? (
                            <img src={gift.imageUrl} alt={gift.name} className="w-full h-full object-cover rounded-lg" />
                          ) : (
                            <Gift size={32} className="text-dark-400" />
                          )}
                        </div>
                        <h3 className="font-semibold text-white">{gift.name}</h3>
                        <p className="text-xs text-dark-400 mt-1">数量: {item.quantity}</p>
                      </div>

                      <button
                        onClick={() => handleGiveGift(item.giftId)}
                        className="w-full px-4 py-2 bg-gradient-to-r from-primary to-secondary hover:from-primary/80 hover:to-secondary/80 text-white rounded-lg transition-all text-sm font-medium shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transform hover:scale-105"
                      >
                        💝 送给{character.name}
                      </button>
                    </div>
                  )
                })
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

